
'use client';

import * as React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ImageIcon, ArrowRight, AlertTriangle } from 'lucide-react';
import Link from 'next/link';
import { useFirestore } from '@/firebase';
import { getAllHybridMedia, type HybridMediaItem } from '@/lib/gallery-hybrid';
import Image from 'next/image';

export default function GalleryCard({ className }: { className?: string }) {
  const firestore = useFirestore();
  const [images, setImages] = React.useState<HybridMediaItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchImages = async () => {
      if (!firestore) return;

      try {
        setLoading(true);
        // Fetch recent media from Firebase
        const mediaItems = await getAllHybridMedia(firestore, { limit: 20 });

        // Shuffle the array and take the first 9 for a random selection
        const shuffled = [...mediaItems].sort(() => 0.5 - Math.random());
        setImages(shuffled.slice(0, 9));

      } catch (err) {
        console.error(err);
        setError('Could not load gallery preview.');
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, [firestore]);

  return (
    <Card className={className} id="gallery">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-primary" />
          Club Gallery
        </CardTitle>
        <CardDescription>
          A glimpse into our club's activities and members' aircraft.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="grid grid-cols-3 gap-2">
            {[...Array(9)].map((_, i) => (
              <Skeleton key={i} className="aspect-square w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center flex-col gap-4 text-center py-10 text-destructive">
            <AlertTriangle className="w-8 h-8" />
            <p>{error}</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {images.map((image) => (
              <Link
                key={image.id}
                href={`/gallery/${image.albumId}`}
                className="relative aspect-square w-full overflow-hidden rounded-md group cursor-pointer"
              >
                <Image
                  src={image.thumbnailUrl || image.url}
                  alt={image.description || image.title}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-110"
                  sizes="(max-width: 768px) 30vw, (max-width: 1200px) 20vw, 15vw"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              </Link>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button asChild className="w-full">
          <Link href="/gallery">
            View Full Gallery
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
